import type React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import {
  DescriptionOutlined,
  OpenInNewOutlined,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import type { IStreamDetails } from "../../../../types/streams";
import { formatDateToLongString } from "../../../../utils/FormatDate";
import apiService from "../../../../service/Api";

interface ServiceDeliveryPanelProps {
  stream: IStreamDetails;
}

const ServiceDeliveryPanel: React.FC<ServiceDeliveryPanelProps> = ({
  stream,
}) => {
  const navigate = useNavigate();
  const [isSigned, setIsSigned] = useState(false);

  useEffect(() => {
    let isMounted = true;
    apiService
      .get<
        { status: string }[] | { results: { status: string }[] }
      >(`/api/streams/service-delivery-note/?stream=${stream.id}`)
      .then((response) => {
        const data = response.data;
        const note = Array.isArray(data) ? data[0] : data.results?.[0];
        if (isMounted) setIsSigned(note?.status === "signed");
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [stream.id]);

  const deceasedName = [
    stream.booking?.deceased?.[0]?.first_name,
    stream.booking?.deceased?.[0]?.surname,
  ]
    .filter(Boolean)
    .join(" ");

  const label = deceasedName
    ? `Service-Delivery-${deceasedName.replace(/\s+/g, "-")}.pdf`
    : "Service-Delivery-Note.pdf";

  const dateLabel = stream.date ? formatDateToLongString(stream.date) : "";

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: 2.5,
        boxShadow: "none",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#000" }}>
          Service Delivery
        </Typography>
        {isSigned && (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Signed"
            color="success"
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {/* Single document row */}
      <Box
        onClick={() =>
          navigate(`/stream-management/view/${stream.id}/service-delivery-note`)
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          borderRadius: "12px",
          backgroundColor: "#f5f7fa",
          cursor: "pointer",
          "&:hover": { backgroundColor: "#edf0f4" },
        }}
      >
        {/* Icon badge */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: "#fef3c7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 22, color: "#d97706" }} />
        </Box>

        {/* Label + date */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            fontWeight="600"
            noWrap
            sx={{ color: "#111" }}
          >
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {dateLabel}
          </Typography>
        </Box>

        {/* Open icon */}
        <Tooltip title="View note">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/stream-management/view/${stream.id}/service-delivery-note`,
              );
            }}
            sx={{ color: "#9ca3af" }}
          >
            <OpenInNewOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ServiceDeliveryPanel;
